import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';

import { filesAPIService } from '~/api/files';
import AppleDark from '~/assets/icons/appleDark.png';
import GooglePlay from '~/assets/icons/googlePlay.png';
import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import { appsSlice } from '~/store/reducers/previewScreens/apps';
import { updateAppSiteThunk } from '~/store/reducers/previewScreens/apps';
import { getAppsReducer } from '~/store/reducers/previewScreens/apps/selectors';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

const linkListForms = [
  {
    label: 'appName',
    value: 'Example',
    id: 'appName',
    requestName: 'name',
    required: true,
    maxLength: 100,
    placeholder: 'common:enterAppName',
  },
  {
    label: 'developerCompany',
    value: 'Example',
    id: 'developerCompany',
    requestName: 'developer',
    maxLength: 100,
    placeholder: 'common:enterDescription',
  },
  {
    label: 'description',
    value: 'Example',
    id: 'description',
    requestName: 'description',
    maxLength: 200,
    placeholder: 'common:enterDescription',
  },
  {
    label: 'website',
    value: 'Example',
    id: 'website',
    requestName: 'website',
    validation: true,
    maxLength: 2000,
  },
];

const linkSocialForms = [
  {
    label: 'Google',
    icon: <img src={GooglePlay} alt="google play" />,
    value: 'https://...',
    id: 'google',
    requestName: 'android',
    maxLength: 2000,
  },
  {
    label: 'Apple',
    icon: <img src={AppleDark} alt="apple dark" />,
    value: 'https://...',
    id: 'apple',
    requestName: 'iphone',
    maxLength: 2000,
  },
];

const { actions } = appsSlice;

export function BasicInformation() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const appsReducer = useSelector(getAppsReducer);
  const isDesktop = useSelector(getIsDesktop);

  const { appInformation, platformLinks, micrositeData, logo } = appsReducer;

  const createImagePreviewUrl = async (event, isRemove) => {
    const file = event?.target?.files[0];
    const url = isRemove ? null : window?.URL?.createObjectURL(file);

    try {
      let storageResponse;

      if (!isRemove) {
        storageResponse = await filesAPIService.uploadImage(file);
      }

      dispatch(actions.setLogo(isRemove ? '' : { path: url, imageStorageId: storageResponse.data }));
      dispatch(
        updateAppSiteThunk({
          id: micrositeData?.id,
          body: { logo_id: isRemove ? null : storageResponse.data },
        }),
      );
    } catch (e) {
      logger.error(e);
    }
  };

  const handleChangeLinks = (item, value) => {
    dispatch(actions.setPlatformLinks({ field: item.id, value }));
  };

  return (
    <CreateQRContentBlockLayout type="basicInfo">
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('template3:appInfo')}
        details={
          <>
            <ImageUploader
              image={logo?.path}
              onDeleteImage={() => createImagePreviewUrl({}, true)}
              onUploadImage={createImagePreviewUrl}
            />
            {linkListForms.map((item, index) => {
              return item.id === 'website' ? (
                <UrlTextField
                  requireProtocol={false}
                  urlValue={appInformation[item.id]}
                  labelSx={{ marginTop: '12px' }}
                  key={item.label + index}
                  onChange={(e) => {
                    dispatch(actions.setAppInformation({ field: item.id, value: e.target.value }));
                  }}
                  label={t('template2:seconTitleLink')}
                  required={false}
                  defaultValue={appInformation[item.id]}
                  onBlur={({ isValid }) =>
                    isValid &&
                    dispatch(
                      updateAppSiteThunk({
                        id: micrositeData?.id,
                        body: { [item.requestName]: appInformation[item.id] },
                      }),
                    )
                  }
                />
              ) : (
                <TextInput
                  onChange={(e) => dispatch(actions.setAppInformation({ field: item.id, value: e.target.value }))}
                  key={item.label + index}
                  defaultValue={appInformation[item.id]}
                  labelSx={{ marginTop: '12px' }}
                  fullWidth
                  label={`${t(`other:${item?.label}`)}${item.required ? '*' : ''}`}
                  placeholder={t(item.placeholder)}
                  onBlur={() =>
                    dispatch(
                      updateAppSiteThunk({
                        id: micrositeData?.id,
                        body: { [item.requestName]: appInformation[item.id] },
                      }),
                    )
                  }
                  inputProps={{ maxLength: item.maxLength }}
                  error={item.required && !appInformation[item.id]}
                  helperText={
                    (item.required && !appInformation[item.id] && t('inputs:required')) ||
                    isMaxLength(appInformation[item.id], item.maxLength)
                  }
                />
              );
            })}
          </>
        }
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('basicTemplateLabels:socilaNet')}
        hideBottomBorder
        details={
          <>
            {linkSocialForms.map((item) => {
              return (
                <Box
                  key={item.id}
                  display="flex"
                  sx={{
                    marginBottom: item.id === 'google' ? '12px' : undefined,
                    gap: '16px',
                    flexDirection: isDesktop ? 'row' : 'column',
                    alignItems: isDesktop ? 'center' : 'flex-start',
                  }}
                >
                  <Box display="flex" alignItems="center" sx={{ width: 160, gap: '16px' }}>
                    {item.icon}
                    <Text variant="t16Regular">{item.label}</Text>
                  </Box>
                  <UrlTextField
                    requireProtocol={false}
                    urlValue={platformLinks[item.id]}
                    placeholder={item.label}
                    inputProps={{ maxLength: item.maxLength }}
                    onChange={(e) => handleChangeLinks(item, e.target.value)}
                    defaultValue={platformLinks[item.id]}
                    required={false}
                    onBlur={() => {
                      dispatch(
                        updateAppSiteThunk({
                          id: micrositeData?.id,
                          body: { app: { [item.requestName]: platformLinks[item.id] } },
                        }),
                      );
                    }}
                  />
                </Box>
              );
            })}
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
